import { useState } from 'react';

import PropTypes from 'prop-types';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Tag from '#globalComponents/Tag/Tag';
import { formatCents } from '#lib/numberFormat';
import { sanitizeHtml } from '#lib/sanitize';

import { usePlanSelect } from '../../../../models/billing/mutations';
import { usePlansWithPrices } from '../../../../models/billing/queries';
import { PlanPrice } from '../PlanPrice/PlanPrice';

const resolveBadge = ({ isActive, isHighlighted }) => {
	if (isActive) return <Tag tone="success" label="Ihr aktueller Plan" />;
	if (isHighlighted) return <Tag tone="primary" label="Am meisten gekauft" />;
	return null;
};

const PlanCard = ({ plan, isActive, isHighlighted, onSelect, isPending, isLocked }) => {
	const buttonLabel = isActive ? 'Aktueller Plan' : isLocked ? 'Derzeit nicht möglich' : 'Plan wählen';

	return (
		<Card className={`flex h-full flex-col overflow-hidden p-0 ${isHighlighted ? 'border-primary-500' : ''}`}>
			<div
				className={`border-b px-5 py-4 ${
					isHighlighted ? 'border-primary-200 bg-primary-100' : 'border-bgBase-500 bg-bgBase-300'
				}`}
			>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<h3 className="text-md-bold text-text">{plan.name}</h3>
					{resolveBadge({ isActive, isHighlighted })}
				</div>
				<PlanPrice amountCents={plan.monthlyPrice?.unitAmount} isHighlighted={isHighlighted} className="mt-2" />
				{plan.shortDescription && <p className="mt-2 text-sm text-bgBase-800">{plan.shortDescription}</p>}
			</div>

			<div className="flex flex-1 flex-col px-5 py-4">
				<div
					className="
					text-sm
					text-text
					leading-[2.5em]
					[&_ul]:list-inside [&_ul]:pl-0
					[&_ul>li::marker]:content-['✓_']
					[&_ul>li::marker]:text-primary-600
					"
					dangerouslySetInnerHTML={{ __html: sanitizeHtml(plan.description) }}
				/>

				<div className="mt-auto pt-4">
					<Button
						variant={isHighlighted ? 'solid' : 'outline'}
						onClick={() => !isActive && plan.monthlyPrice && onSelect(plan)}
						loading={isPending}
						disabled={isActive || !plan.monthlyPrice || isLocked}
						fullWidth
					>
						{buttonLabel}
					</Button>
				</div>
			</div>
		</Card>
	);
};

PlanCard.propTypes = {
	plan: PropTypes.object.isRequired,
	isActive: PropTypes.bool.isRequired,
	isHighlighted: PropTypes.bool.isRequired,
	onSelect: PropTypes.func.isRequired,
	isPending: PropTypes.bool.isRequired,
	isLocked: PropTypes.bool.isRequired,
};

export const PlanSelectionCard = ({ currentSubscription, isLocked = false }) => {
	const { data: plansWithPrices = [], isLoading } = usePlansWithPrices();
	const planSelectMutation = usePlanSelect(currentSubscription);
	const [planToConfirm, setPlanToConfirm] = useState(null);
	const currentPlanAmount = currentSubscription?.stripePrices?.unitAmount ?? null;

	// Without a subscription the mutation redirects to Stripe Checkout, which is its own
	// confirmation step — the modal is only for changing an existing subscription.
	const handlePlanSelect = (plan) => {
		if (isLocked) return;
		if (currentSubscription) {
			setPlanToConfirm(plan);
		} else {
			planSelectMutation.mutate(plan.monthlyPrice.stripePriceId);
		}
	};

	const handleChangeConfirm = () => planSelectMutation.mutateAsync(planToConfirm.monthlyPrice.stripePriceId);

	// An upgrade is applied at once and billed pro rata; a downgrade only takes effect once the
	// period the customer already paid for has run out.
	const isDowngrade =
		planToConfirm && currentPlanAmount !== null && planToConfirm.monthlyPrice.unitAmount < currentPlanAmount;

	if (isLoading) {
		return (
			<div className="flex flex-col items-center py-12">
				<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
				<p className="mt-4 text-sm text-bgBase-800">Lade verfügbare Pakete...</p>
			</div>
		);
	}

	return (
		<>
			{isLocked && (
				<Alert tone="info" className="mb-4">
					Während einer Pause oder laufenden Kündigung ist kein Planwechsel möglich.
				</Alert>
			)}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
				{plansWithPrices.map((plan) => {
					const isActive =
						Boolean(currentSubscription?.priceId) && currentSubscription.priceId === plan.monthlyPrice?.id;
					const isHighlighted = isActive || plan.name === 'Business Plan';

					return (
						<PlanCard
							key={plan.key}
							plan={plan}
							isActive={isActive}
							isHighlighted={isHighlighted}
							onSelect={handlePlanSelect}
							isPending={planSelectMutation.isPending}
							isLocked={isLocked}
						/>
					);
				})}
			</div>

			<ConfirmModal
				isOpen={planToConfirm !== null}
				onClose={() => setPlanToConfirm(null)}
				onConfirm={handleChangeConfirm}
				title="Planwechsel bestätigen"
				message={
					planToConfirm
						? `Möchten Sie Ihr Abonnement auf den Plan „${planToConfirm.name}“ (${formatCents(planToConfirm.monthlyPrice.unitAmount)}/Monat) umstellen?`
						: ''
				}
				subtitle={
					isDowngrade
						? 'Der Wechsel wird zum Ende Ihrer aktuellen Abrechnungsperiode wirksam. Bis dahin behalten Sie Ihren jetzigen Plan.'
						: 'Die Umstellung wird sofort wirksam und Ihre Abrechnung entsprechend angepasst.'
				}
				confirmText="Plan wechseln"
				color="primary"
				isLoading={planSelectMutation.isPending}
			/>
		</>
	);
};

PlanSelectionCard.propTypes = {
	currentSubscription: PropTypes.object,
	isLocked: PropTypes.bool,
};
